import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import FocusTrap from 'focus-trap-react';
import cx from 'classnames';

import iconClose from '../../../assets/svg/icon-cross.svg';

const Modal = ({
  isOpen = false,
  size = 'md',
  triggerLabel,
  closeLabel = 'Close Modal',
  className,
  classNameTrigger,
  children,
  container = document.body,
  toggleClass,
  toggleEvent,
  closeEvent,
}) => {
  const [open, setOpen] = useState(isOpen);

  const toggleScrollLock = () => document.querySelector('html')?.classList.toggle('scroll-lock');

  const removeScrollLock = () => document.querySelector('html')?.classList.remove('scroll-lock');

  const toggleOpenClass = () => {
    const toggleClassString = toggleClass ? `${toggleClass}-` : '';
    document.querySelector('html')?.classList.toggle(`modal-${toggleClassString}open`);
  };

  const removeOpenClass = () => {
    const toggleClassString = toggleClass ? `${toggleClass}-` : '';
    document.querySelector('html')?.classList.remove(`modal-${toggleClassString}open`);
  };

  const closeModal = () => {
    setOpen(false);
    removeScrollLock();
    removeOpenClass();
  };

  const toggleModal = () => {
    setOpen(!open);
    toggleScrollLock();
    toggleOpenClass();
  };

  const handleEscKey = (event) => event.keyCode === 27 && closeModal();

  const handleOutsideClick = (event) => event.target === event.currentTarget && closeModal();

  /**
   * The below useEffect() allows the modal to be toggled with a custom event; toggleEvent.
   */
  useEffect(() => {
    if (!toggleEvent) return;
    document.addEventListener(toggleEvent, toggleModal);
    return () => document.removeEventListener(toggleEvent, toggleModal);
  }, [open]);

  useEffect(() => {
    if (!closeEvent) return;
    document.addEventListener(closeEvent, closeModal);
    return () => document.removeEventListener(closeEvent, closeModal);
  }, []);

  return (
    <>
      <button onClick={toggleModal} className={cx('modal__trigger', classNameTrigger)} type="button">
        {triggerLabel}
      </button>

      {!open && null}

      {open &&
        createPortal(
          <>
            <FocusTrap
              focusTrapOptions={{
                allowOutsideClick: () => true,
              }}
            >
              {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
              <div
                className={cx(className, 'modal', size === 'sm' && 'modal--sm', size === 'md' && 'modal--md')}
                role="dialog"
                tabIndex={-1}
                aria-modal="true"
                onClick={handleOutsideClick}
                onKeyDown={handleEscKey}
              >
                {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
                <div className="modal__dialog">
                  <div className="modal__dialog-container container">
                    <button className="modal__close-button" onClick={closeModal} aria-label={closeLabel} type="button">
                      <span dangerouslySetInnerHTML={{__html: iconClose}} />
                    </button>
                    <div className="modal__body">{children}</div>
                  </div>
                </div>
              </div>
            </FocusTrap>
            <div className="fader fader--search" onClick={closeModal} onKeyDown={handleEscKey} />
          </>,
          container
        )}
    </>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'full-screen']),
  triggerLabel: PropTypes.node,
  closeLabel: PropTypes.string,
  className: PropTypes.string,
  classNameTrigger: PropTypes.string,
  children: PropTypes.node,
  container: PropTypes.node,
  toggleEvent: PropTypes.string,
  closeEvent: PropTypes.string,
  toggleClass: PropTypes.string,
};

export default Modal;
